import { AI_CREDIT_UNLIMITED, AiCreditUsage } from "@/shared/domain/limits";

const ONE_MILLION = 1_000_000;

/**
 * What one agent unit costs Roboto. Credits are defined against vendor list
 * price (see ``roboto_internal.ai_credits.pricing``): 1 RAU = $0.10.
 */
export const USD_PER_AGENT_UNIT = 0.1;

/** Roboto's cost, in USD, of the given agent-unit millionths. */
export function agentUnitCostUsd(millionths: number): number {
  return (millionths / ONE_MILLION) * USD_PER_AGENT_UNIT;
}

/**
 * Convert a monthly AI credit usage snapshot into the display strings the
 * Agent Units card on Settings → Usage renders.
 *
 * Returns the used-units label (always two decimals, in RAU — the same shape the
 * processing-units card beside it uses), the limit label, and the used/limit
 * percentage. `currentPercent` is intentionally
 * unclamped: the UsageLimit component clamps the progress bar fill to
 * [0, 100] for layout, but the numeric "X% of Y" label stays honest so a
 * user who has burned past their cap sees the true overage.
 *
 * Two non-default branches:
 * - Unlimited tier: when the limit equals the AI_CREDIT_UNLIMITED sentinel
 *   (-1, matching the backend QUANTITY_UNLIMITED encoding), the limit
 *   shows as "Unlimited" and the percentage reports 0 so the bar reads as
 *   empty rather than full.
 * - Non-positive limit: a zero or negative finite limit would be a
 *   misconfigured tier override and would otherwise produce NaN or
 *   Infinity from the division. The percentage collapses to 0 so the card
 *   still renders cleanly instead of breaking the layout.
 */
export function formatAiCreditUsage(usage: AiCreditUsage): {
  currentValue: string;
  limitValue: string;
  currentPercent: number;
} {
  const usedCredits = usage.used_millionths / ONE_MILLION;
  const currentValue = `${usedCredits.toFixed(2)} RAU`;

  if (usage.limit_millionths === AI_CREDIT_UNLIMITED) {
    return { currentValue, limitValue: "Unlimited", currentPercent: 0 };
  }

  const limitCredits = usage.limit_millionths / ONE_MILLION;
  const currentPercent =
    limitCredits > 0 ? (usedCredits / limitCredits) * 100 : 0;

  return {
    currentValue,
    // Strip trailing zeros so the label reads "10 RAU", not "10.00 RAU".
    limitValue: `${+limitCredits.toFixed(2)} RAU`,
    currentPercent,
  };
}
